// Excel export utility for applications and resumes
import * as XLSX from 'xlsx';
import type { StoredApplication } from './applicationStorage';
import type { StoredResume } from './resumeStorage';

/**
 * Export applications to Excel with specified column order
 * @param applications - Array of applications to export (can be filtered)
 * @param filename - Name of the file to download
 */
export const exportApplicationsToExcel = (
  applications: StoredApplication[],
  filename: string = 'applications_export.xlsx'
) => {
  // Map applications to desired column order
  const data = applications.map((app, index) => ({
    'S.No': index + 1,
    'Full Name': app.name || '',
    'Email Address': app.email || '',
    'Mobile Number': app.phone || '',
    'Date of Birth': app.dob || '',
    'LinkedIn Profile URL': app.linkedinUrl || '',
    'Portfolio URL': app.portfolioUrl || '',
    'Current Address': app.currentAddress || '',
    'Highest Qualification': app.qualification || '',
    'Specialization': app.specialization || '',
    'University/College Name': app.university || '',
    'Year of Passing': app.yearOfPassing || '',
    'Experience Level': app.experienceLevel || '',
    'Total Experience (Years)': app.totalExperience || app.experience || '',
    'Current/Previous Role': app.previousJobTitle || '',
    'Current CTC': app.currentCtc || '',
    'Expected Salary': app.expectedSalary || '',
    'Notice Period': app.noticePeriod || '',
    'Preferred Location': app.preferredLocation || '',
    'Willing to Relocate': app.willingToRelocate || '',
    'Skills': Array.isArray(app.skills) ? app.skills.join(', ') : app.skills || '',
    'Phone': app.phone || '',
    'Position': app.position || '',
    'Career ID': app.jobId || '',
    'Experience': app.experience || '',
    'Cover Letter': app.coverLetter || '',
    'Status': app.status || '',
    'Priority': app.rating ? `${app.rating} stars` : '',
    'Assigned To': '', // Not in current schema
    'Tags': '', // Not in current schema
    'Archived': '', // Not in current schema
    'Visibility': '', // Not in current schema
    'Created At': app.appliedDate ? formatDate(app.appliedDate) : '',
    'Updated At': '', // Not in current schema
  }));

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Set column widths for better readability
  const columnWidths = [
    { wch: 6 },  // S.No
    { wch: 20 }, // Full Name
    { wch: 25 }, // Email Address
    { wch: 15 }, // Mobile Number
    { wch: 12 }, // Date of Birth
    { wch: 30 }, // LinkedIn Profile URL
    { wch: 30 }, // Portfolio URL
    { wch: 30 }, // Current Address
    { wch: 20 }, // Highest Qualification
    { wch: 20 }, // Specialization
    { wch: 25 }, // University/College Name
    { wch: 15 }, // Year of Passing
    { wch: 15 }, // Experience Level
    { wch: 20 }, // Total Experience (Years)
    { wch: 25 }, // Current/Previous Role
    { wch: 15 }, // Current CTC
    { wch: 15 }, // Expected Salary
    { wch: 15 }, // Notice Period
    { wch: 20 }, // Preferred Location
    { wch: 18 }, // Willing to Relocate
    { wch: 40 }, // Skills
    { wch: 15 }, // Phone
    { wch: 25 }, // Position
    { wch: 20 }, // Career ID
    { wch: 20 }, // Experience
    { wch: 40 }, // Cover Letter
    { wch: 12 }, // Status
    { wch: 12 }, // Priority
    { wch: 15 }, // Assigned To
    { wch: 20 }, // Tags
    { wch: 10 }, // Archived
    { wch: 12 }, // Visibility
    { wch: 18 }, // Created At
    { wch: 18 }, // Updated At
  ];
  worksheet['!cols'] = columnWidths;

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Applications');

  // Generate Excel file and trigger download
  XLSX.writeFile(workbook, filename);
};

/**
 * Export resumes to Excel with specified column order
 * @param resumes - Array of resumes to export (can be filtered)
 * @param filename - Name of the file to download
 */
export const exportResumesToExcel = (
  resumes: StoredResume[],
  filename: string = 'resumes_export.xlsx'
) => {
  // Map resumes to desired column order
  const data = resumes.map((resume, index) => ({
    'S.No': index + 1,
    'Full Name': resume.name || '',
    'Email Address': resume.email || '',
    'Mobile Number': resume.phone || '',
    'Date of Birth': resume.dob || '',
    'LinkedIn Profile URL': resume.linkedIn || '',
    'Portfolio URL': resume.portfolio || '',
    'Current Address': resume.currentAddress || '',
    'Highest Qualification': resume.qualification || '',
    'Specialization': resume.specialization || '',
    'University/College Name': resume.university || '',
    'Year of Passing': resume.yearOfPassing || '',
    'Experience Level': '', // Not directly in resume schema
    'Total Experience (Years)': resume.totalExperience || resume.experience || '',
    'Current/Previous Role': resume.currentRole || resume.previousJobTitle || '',
    'Current CTC': resume.currentCtc || '',
    'Expected Salary': resume.expectedSalary || '',
    'Notice Period': resume.noticePeriod || '',
    'Preferred Location': resume.preferredLocation || '',
    'Willing to Relocate': resume.willingToRelocate || '',
    'Skills': Array.isArray(resume.skills) ? resume.skills.join(', ') : resume.skills || '',
    'Phone': resume.phone || '',
    'Position': '', // Not in resume schema
    'Career ID': '', // Not in resume schema
    'Experience': resume.experience || '',
    'Cover Letter': '', // Not in resume schema
    'Status': resume.status || '',
    'Priority': resume.rating ? `${resume.rating} stars` : '',
    'Assigned To': '', // Not in current schema
    'Tags': '', // Not in current schema
    'Archived': '', // Not in current schema
    'Visibility': '', // Not in current schema
    'Created At': resume.submittedDate || resume.uploadedDate ? formatDate(resume.submittedDate || resume.uploadedDate!) : '',
    'Updated At': '', // Not in current schema
  }));

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Set column widths for better readability
  const columnWidths = [
    { wch: 6 },  // S.No
    { wch: 20 }, // Full Name
    { wch: 25 }, // Email Address
    { wch: 15 }, // Mobile Number
    { wch: 12 }, // Date of Birth
    { wch: 30 }, // LinkedIn Profile URL
    { wch: 30 }, // Portfolio URL
    { wch: 30 }, // Current Address
    { wch: 20 }, // Highest Qualification
    { wch: 20 }, // Specialization
    { wch: 25 }, // University/College Name
    { wch: 15 }, // Year of Passing
    { wch: 15 }, // Experience Level
    { wch: 20 }, // Total Experience (Years)
    { wch: 25 }, // Current/Previous Role
    { wch: 15 }, // Current CTC
    { wch: 15 }, // Expected Salary
    { wch: 15 }, // Notice Period
    { wch: 20 }, // Preferred Location
    { wch: 18 }, // Willing to Relocate
    { wch: 40 }, // Skills
    { wch: 15 }, // Phone
    { wch: 25 }, // Position
    { wch: 20 }, // Career ID
    { wch: 20 }, // Experience
    { wch: 40 }, // Cover Letter
    { wch: 12 }, // Status
    { wch: 12 }, // Priority
    { wch: 15 }, // Assigned To
    { wch: 20 }, // Tags
    { wch: 10 }, // Archived
    { wch: 12 }, // Visibility
    { wch: 18 }, // Created At
    { wch: 18 }, // Updated At
  ];
  worksheet['!cols'] = columnWidths;

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Resumes');

  // Generate Excel file and trigger download
  XLSX.writeFile(workbook, filename);
};

/**
 * Format date string to readable format
 */
const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    // Format: DD/MM/YYYY HH:MM:SS
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    return dateString;
  }
};
